import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import {
  Bell,
  BellRing,
  Check,
  CheckCheck,
  CheckCircle2,
  AlertTriangle,
  AlertCircle,
  Info,
  Settings,
  X
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { io } from 'socket.io-client';
import { useTimezone } from '@/contexts/TimezoneContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SOCKET_PATH = '/api/socket.io';

const severityConfig = {
  critical: { icon: AlertCircle, color: 'text-red-500', bg: 'bg-red-500/20', border: 'border-red-500', label: 'Crítica' },
  high: { icon: AlertTriangle, color: 'text-orange-500', bg: 'bg-orange-500/20', border: 'border-orange-500', label: 'Alta' },
  medium: { icon: AlertTriangle, color: 'text-yellow-500', bg: 'bg-yellow-500/20', border: 'border-yellow-500', label: 'Média' },
  low: { icon: Info, color: 'text-blue-500', bg: 'bg-blue-500/20', border: 'border-blue-500', label: 'Baixa' },
};

const AlertCenter = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [activeCount, setActiveCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchAlerts = useCallback(async () => {
    try {
      const [alertsRes, countRes] = await Promise.all([
        axios.get(`${API}/alerts?limit=50`),
        axios.get(`${API}/alerts/active/count`)
      ]);
      setAlerts(alertsRes.data);
      setActiveCount(countRes.data.count);
    } catch (error) {
      console.error('Error fetching alerts:', error);
    }
  }, []);

  useEffect(() => {
    fetchAlerts();

    // Setup Socket.IO for real-time alerts
    const socket = io(process.env.REACT_APP_BACKEND_URL, { 
      path: SOCKET_PATH,
      transports: ['websocket', 'polling']
    });

    socket.on('new_alert', (alert) => {
      setAlerts(prev => [alert, ...prev.slice(0, 49)]);
      setActiveCount(prev => prev + 1);
    });

    socket.on('alert_updated', (updatedAlert) => {
      setAlerts(prev =>
        prev.map(a => a.id === updatedAlert.id ? updatedAlert : a)
      );
      if (updatedAlert.status !== 'active') {
        setActiveCount(prev => Math.max(0, prev - 1));
      }
    });

    // Payload agregado {action, matched, modified} — sem docs individuais, tratar como
    // sinal de refetch (lista + count numa tacada). Cobre resolve-all/acknowledge-all
    // manuais e auto-resolves do backend.
    socket.on('alert_bulk_updated', () => {
      fetchAlerts();
    });

    return () => {
      socket.disconnect();
    };
  }, [fetchAlerts]);

  const handleAcknowledge = async (alertId) => {
    setLoading(true);
    try {
      await axios.put(`${API}/alerts/${alertId}/acknowledge`);
      fetchAlerts();
    } catch (error) {
      console.error('Error acknowledging alert:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResolve = async (alertId) => {
    setLoading(true);
    try {
      await axios.put(`${API}/alerts/${alertId}/resolve`);
      fetchAlerts();
    } catch (error) {
      console.error('Error resolving alert:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfigureSource = useCallback((alert) => {
    const sourceGroupId = alert?.metadata?.source_group_id;
    const actionUrl = alert?.action_url || (
      alert?.equipment_id && sourceGroupId
        ? `/equipments/${alert.equipment_id}/sources?sourceGroupId=${sourceGroupId}&configure=1`
        : null
    );
    if (!actionUrl) return;
    setOpen(false);
    navigate(actionUrl);
  }, [navigate]);

  const handleIgnoreSource = async (alert) => {
    const sourceGroupId = alert?.metadata?.source_group_id;
    if (!alert?.equipment_id || !sourceGroupId) return;
    setLoading(true);
    try {
      await axios.post(`${API}/equipments/${alert.equipment_id}/source-groups/${sourceGroupId}/ignore`);
      fetchAlerts();
    } catch (error) {
      console.error('Error ignoring detected source:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAcknowledgeAll = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/alerts/acknowledge-all`, null, { params: { status: 'active' } });
      fetchAlerts();
    } catch (error) {
      console.error('Error acknowledging alerts in bulk:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResolveAll = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/alerts/resolve-all`);
      fetchAlerts();
    } catch (error) {
      console.error('Error resolving alerts in bulk:', error);
    } finally {
      setLoading(false);
    }
  };

  const activeAlerts = alerts.filter(a => a.status === 'active');
  const acknowledgedAlerts = alerts.filter(a => a.status === 'acknowledged');
  // D-20 (Phase 17.1 Plan 09): alerta critical que auto-resolveu SEM ciência prévia —
  // fica em seção própria destacada (nunca esmaecida), separada do "Encerrado" normal,
  // até alguém dar ciência (o backend já entrega isso na visão padrão default).
  const isUnackedResolved = (a) => a.status === 'resolved' && a.resolved_without_ack && !a.acknowledged_at;
  const unackedResolvedAlerts = alerts.filter(isUnackedResolved);
  const resolvedAlerts = alerts.filter(a => a.status === 'resolved' && !isUnackedResolved(a)).slice(0, 10);
  const actionableActiveAlerts = activeAlerts.filter(a => a.alert_type !== 'source_detected');
  const actionableAcknowledgedAlerts = acknowledgedAlerts.filter(a => a.alert_type !== 'source_detected');

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (nextOpen) fetchAlerts();
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          data-testid="alert-center-btn"
        >
          {activeCount > 0 ? (
            <BellRing className="w-5 h-5 text-yellow-500 animate-pulse" />
          ) : (
            <Bell className="w-5 h-5" />
          )}
          {activeCount > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
              {activeCount > 9 ? '9+' : activeCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[min(24rem,calc(100vw-2rem))] p-0"
        align="end"
        data-testid="alert-center-popover"
      >
        <TooltipProvider>
          <div className="p-4 border-b border-border">
            <h3 className="font-semibold text-lg flex items-center gap-2">
              <Bell className="w-5 h-5 text-primary" />
              {t('alertCenter')}
            </h3>
            <p className="text-sm text-muted-foreground">
              {activeCount} {activeCount !== 1 ? t('activeAlertsPlural') : t('activeAlerts')}
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs"
                    onClick={handleAcknowledgeAll}
                    disabled={loading || actionableActiveAlerts.length === 0}
                    data-testid="alert-center-acknowledge-all-btn"
                  >
                    <Check className="w-3 h-3 mr-1" />
                    Ciente em todos
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  Não volta enquanto durar — some da lista até a condição cessar (o sistema encerra sozinho quando cessa).
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    className="h-7 text-xs"
                    onClick={handleResolveAll}
                    disabled={loading || (actionableActiveAlerts.length + actionableAcknowledgedAlerts.length === 0)}
                    data-testid="alert-center-resolve-all-btn"
                  >
                    <CheckCheck className="w-3 h-3 mr-1" />
                    Encerrar todos
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  Fecha agora. Se a condição persistir ou voltar, um novo alerta é criado.
                </TooltipContent>
              </Tooltip>
            </div>
          </div>

          <ScrollArea className="h-[400px]">
            {alerts.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                <Bell className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p>{t('noAlerts')}</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {/* Active Alerts */}
                {activeAlerts.length > 0 && (
                  <div className="p-2">
                    <p className="text-xs font-medium text-muted-foreground px-2 py-1">
                      {t('alertsActive')} ({activeAlerts.length})
                    </p>
                    {activeAlerts.map((alert) => (
                      <AlertItem
                        key={alert.id}
                        alert={alert}
                        onAcknowledge={handleAcknowledge}
                        onResolve={handleResolve}
                        onConfigureSource={handleConfigureSource}
                        onIgnoreSource={handleIgnoreSource}
                        loading={loading}
                        t={t}
                      />
                    ))}
                  </div>
                )}

                {/* Acknowledged Alerts */}
                {acknowledgedAlerts.length > 0 && (
                  <div className="p-2">
                    <div className="flex items-center gap-2 px-2 py-1">
                      <Badge variant="secondary" className="text-xs text-muted-foreground gap-1">
                        <CheckCircle2 className="w-3 h-3" />
                        {t('alertsAcknowledged')}
                      </Badge>
                      <span className="text-xs text-muted-foreground">({acknowledgedAlerts.length})</span>
                    </div>
                    {acknowledgedAlerts.map((alert) => (
                      <AlertItem
                        key={alert.id}
                        alert={alert}
                        onAcknowledge={handleAcknowledge}
                        onResolve={handleResolve}
                        onConfigureSource={handleConfigureSource}
                        onIgnoreSource={handleIgnoreSource}
                        loading={loading}
                        t={t}
                      />
                    ))}
                  </div>
                )}

                {/* Encerrado sem ciência (D-20) — nunca esmaecido, distinto do "Encerrado" normal */}
                {unackedResolvedAlerts.length > 0 && (
                  <div className="p-2">
                    <div className="flex items-center gap-2 px-2 py-1">
                      <Badge variant="destructive" className="text-xs gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        Encerrado sem ciência
                      </Badge>
                      <span className="text-xs text-muted-foreground">({unackedResolvedAlerts.length})</span>
                    </div>
                    {unackedResolvedAlerts.map((alert) => (
                      <AlertItem
                        key={alert.id}
                        alert={alert}
                        onAcknowledge={handleAcknowledge}
                        onResolve={handleResolve}
                        onConfigureSource={handleConfigureSource}
                        onIgnoreSource={handleIgnoreSource}
                        loading={loading}
                        t={t}
                      />
                    ))}
                  </div>
                )}

                {/* Resolved Alerts */}
                {resolvedAlerts.length > 0 && (
                  <div className="p-2 opacity-60">
                    <p className="text-xs font-medium text-muted-foreground px-2 py-1">
                      {t('alertsResolved')} ({t('alertsResolvedLast')})
                    </p>
                    {resolvedAlerts.map((alert) => (
                      <AlertItem
                        key={alert.id}
                        alert={alert}
                        onAcknowledge={handleAcknowledge}
                        onResolve={handleResolve}
                        onConfigureSource={handleConfigureSource}
                        onIgnoreSource={handleIgnoreSource}
                        loading={loading}
                        t={t}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </ScrollArea>

          {/* Link to full AlertsPage */}
          <div className="p-2 border-t border-border">
            <Link to="/alerts" onClick={() => setOpen(false)}>
              <Button variant="ghost" className="w-full text-sm" data-testid="view-all-alerts-link">
                Ver todos os alertas →
              </Button>
            </Link>
          </div>
        </TooltipProvider>
      </PopoverContent>
    </Popover>
  );
};

const AlertItem = ({ alert, onAcknowledge, onResolve, onConfigureSource, onIgnoreSource, loading, t }) => {
  const { formatDateTime } = useTimezone();
  const config = severityConfig[alert.severity] || severityConfig.medium;
  const Icon = config.icon;
  const isSourceDetected = alert.alert_type === 'source_detected';
  const isActiveSourceDetected = isSourceDetected && alert.status === 'active';
  // D-20: alerta critical encerrado SEM ciência prévia — o botão Ciente continua
  // disponível mesmo com status='resolved' (dar ciência não reabre, só remove da
  // visão padrão); consumido do backend, nunca recomputado (INV-UI-TRAIL-001).
  const isUnackedCritical = alert.status === 'resolved' && alert.resolved_without_ack && !alert.acknowledged_at;
  
  const formatTime = (isoString) => {
    const date = new Date(isoString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return `${t('now')} • ${formatDateTime(isoString, { omitSeconds: true })}`;
    if (diff < 3600000) return `${Math.floor(diff / 60000)} ${t('minutesAgo')} • ${formatDateTime(isoString, { omitSeconds: true })}`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)} ${t('hoursAgo')} • ${formatDateTime(isoString, { omitSeconds: true })}`;
    return formatDateTime(isoString, { omitSeconds: true });
  };

  return (
    <div 
      className={cn(
        'p-3 rounded-lg m-1 border-l-4 transition-all',
        config.bg,
        config.border,
        isActiveSourceDetected && 'cursor-pointer hover:brightness-110'
      )}
      onClick={() => {
        if (isActiveSourceDetected) onConfigureSource(alert);
      }}
      data-testid={`alert-item-${alert.id}`}
    >
      <div className="flex items-start gap-3">
        <Icon className={cn('w-5 h-5 mt-0.5 flex-shrink-0', config.color)} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-medium text-sm truncate">{alert.equipment_name}</span>
            <Badge variant="outline" className="text-xs">
              {config.label || alert.severity}
            </Badge>
            {isUnackedCritical && (
              <Badge variant="destructive" className="text-xs gap-1 shrink-0">
                <AlertTriangle className="w-3 h-3" />
                Sem ciência
              </Badge>
            )}
          </div>
          <p className="text-sm text-foreground/80">{alert.message}</p>
          <p className="text-xs text-muted-foreground mt-1">
            {formatTime(alert.triggered_at)}
          </p>
        </div>
      </div>
      
      {isActiveSourceDetected && (
        <div className="flex flex-wrap gap-2 mt-2 ml-8">
          <Button
            size="sm"
            variant="outline"
            onClick={(event) => {
              event.stopPropagation();
              onConfigureSource(alert);
            }}
            disabled={loading}
            className="h-7 text-xs"
            data-testid={`configure-source-alert-${alert.id}`}
          >
            <Settings className="w-3 h-3 mr-1" />
            {alert.action_label || 'Configurar fonte'}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={(event) => {
              event.stopPropagation();
              onIgnoreSource(alert);
            }}
            disabled={loading}
            className="h-7 text-xs"
            data-testid={`ignore-source-alert-${alert.id}`}
          >
            <X className="w-3 h-3 mr-1" />
            Ignorar fonte
          </Button>
        </div>
      )}

      {!isSourceDetected && (alert.status !== 'resolved' || isUnackedCritical) && (
        <div className="flex flex-wrap gap-2 mt-2 ml-8">
          {(alert.status === 'active' || isUnackedCritical) && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={(event) => {
                    event.stopPropagation();
                    onAcknowledge(alert.id);
                  }}
                  disabled={loading}
                  className="h-7 text-xs"
                  data-testid={`acknowledge-alert-${alert.id}`}
                >
                  <Check className="w-3 h-3 mr-1" />
                  {t('acknowledge')}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {isUnackedCritical
                  ? 'Dar ciência remove este alerta encerrado da visão padrão (não reabre).'
                  : 'Não volta enquanto durar — some da lista até a condição cessar (o sistema encerra sozinho quando cessa).'}
              </TooltipContent>
            </Tooltip>
          )}
          {alert.status !== 'resolved' && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="sm"
                  onClick={(event) => {
                    event.stopPropagation();
                    onResolve(alert.id);
                  }}
                  disabled={loading}
                  className="h-7 text-xs"
                  data-testid={`resolve-alert-${alert.id}`}
                >
                  <CheckCheck className="w-3 h-3 mr-1" />
                  {t('resolve')}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                Fecha agora. Se a condição persistir ou voltar, um novo alerta é criado.
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      )}
    </div>
  );
};

export default AlertCenter;
